import { Navigate, Outlet, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import useAuth from "../hooks/useAuth";
import { isTokenExpired } from "../utils/isTokenExpired";
import useAccessControl from "../hooks/useAccessControl";
import { appAdminRoutes } from "../pages/routes/adminRoutes";
import { appAdminSettingRoutes } from "../pages/routes/adminSettingRoutes";

type Props = {
    allowedRoles: string[];
};

const RequiredAuth = ({ allowedRoles }: Props) => {
    const location = useLocation();
    const { setAuth }: any = useAuth();
    const { permissions, loading }: any = useAccessControl();
    const token = Cookies.get("token-velox-charter");
    const role = Cookies.get("role-velox-charter") || "";
    const expires = Cookies.get("expires-velox-charter");

    if (!token) {
        return <Navigate to="/" state={{ from: location }} replace />;
    }

    if (isTokenExpired(Number(expires))) {
        setAuth(null);

        Cookies.remove("token-velox-charter");
        Cookies.remove("role-velox-charter");
        Cookies.remove("userId-velox-charter");
        Cookies.remove("userName-velox-charter");
        Cookies.remove("fullName-velox-charter");
        Cookies.remove("profileImg-velox-charter");
        Cookies.remove("expires-velox-charter");

        return <Navigate to="/" replace />;
    }

    const isRoleAllowed = allowedRoles?.some(
        (r) => r.toLowerCase() === role.toLowerCase()
    );

    if (isRoleAllowed) {
        return <Outlet />;
    }

    if (loading) {
        return <div className="p-10 text-center">Loading...</div>;
    }

    const permissionValues = Object.values(permissions || {});
    const noPermissions =
        permissionValues.length === 0 ||
        permissionValues.every((value) => value === false);

    if (noPermissions) {
        return <Navigate to="/unauthorized" replace />;
    }

    const currentPath = location.pathname;
    const flattenRoutes = (routes: any[]) => {
        const result: any[] = [];

        routes.forEach(route => {
            if (route.child) {
                route.child.forEach((child: any) => {
                    result.push(child);
                });
            }
            result.push(route);
        });

        return result;
    };

    const allRoutes = flattenRoutes([
        ...appAdminRoutes,
        ...appAdminSettingRoutes
    ]);

    const matchedRoute = allRoutes.find((route: any) =>
        currentPath.startsWith(route.path)
    );

    if (!matchedRoute) {
        return <Navigate to="/unauthorized" replace />;
    }

    const normalizedSection = matchedRoute.section
        .replace(/\s+/g, "")
        .replace(/-/g, "")
        .toLowerCase();
   
    const hasPermission = permissions?.[normalizedSection] === true;

    return hasPermission ? (
        <Outlet />
    ) : (
        <Navigate to="/unauthorized" replace />
    );
};

export default RequiredAuth;